import { ExternalLink } from "lucide-react";
import { useState } from "react";

import { ImageLightbox } from "@/components/cinch/ImageLightbox";
import { cn } from "@/lib/utils";

import type { Entry } from "../api";
import { CategoryBadge } from "./CategoryBadge";

/** Renders one What's New entry. Description is trusted HTML emitted by the
 *  TipTap editor, sanitized at write-time by the admin browser session. */
export function EntryCard({ entry, isUnread }: { entry: Entry; isUnread?: boolean }) {
  const [lightbox, setLightbox] = useState<{ url: string; caption?: string } | null>(
    null,
  );

  return (
    <article
      className={cn(
        "relative rounded-lg border bg-card px-4 py-3 transition-colors",
        isUnread ? "border-brand/40 bg-brand/[0.03]" : "border-border",
      )}
    >
      <header className="mb-2 flex items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <CategoryBadge category={entry.category} />
          <h3 className="text-sm font-semibold leading-tight">{entry.title}</h3>
        </div>
        {entry.build_number && (
          <span className="font-mono text-[10px] text-muted-foreground">
            {entry.build_number}
          </span>
        )}
      </header>

      <div
        className="rich-text text-foreground/90"
        dangerouslySetInnerHTML={{ __html: entry.description }}
      />

      {entry.images && entry.images.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {entry.images.map((img, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setLightbox(img)}
              className="group overflow-hidden rounded-md border border-border transition-shadow hover:shadow-md hover:ring-2 hover:ring-brand/40"
            >
              <img
                src={normalizeUrl(img.url)}
                alt={img.caption || ""}
                className="block h-24 w-auto object-cover"
              />
              {img.caption && (
                <span className="block bg-card px-2 py-1 text-[10px] text-muted-foreground group-hover:text-foreground">
                  {img.caption}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {entry.links && entry.links.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3">
          {entry.links.map((link, i) => (
            <a
              key={i}
              href={link.url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
            >
              {link.label}
              <ExternalLink className="h-3 w-3" />
            </a>
          ))}
        </div>
      )}

      {lightbox && (
        <ImageLightbox
          url={normalizeUrl(lightbox.url)}
          caption={lightbox.caption}
          onClose={() => setLightbox(null)}
        />
      )}
    </article>
  );
}

/** Strip an accidentally-stored origin so the image loads on any host. */
function normalizeUrl(url: string): string {
  if (!url) return url;
  if (url.startsWith("/")) return url;
  try {
    const u = new URL(url);
    return u.pathname + u.search;
  } catch {
    return url;
  }
}
